import type { Problem } from '@ers/sdk';

export type FieldError = { field: string; message: string };

/**
 * Помилка API у форматі RFC 9457 (application/problem+json).
 * `detail` і `errors[].message` сервер формулює українською — інтерфейс показує їх дослівно.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail: string;
  readonly errors: FieldError[];
  readonly requestId?: string;

  constructor(p: { status: number; code: string; title?: string; detail: string; errors?: FieldError[]; requestId?: string }) {
    super(p.detail);
    this.name = 'ApiError';
    this.status = p.status;
    this.code = p.code;
    this.title = p.title ?? '';
    this.detail = p.detail;
    this.errors = p.errors ?? [];
    this.requestId = p.requestId;
  }

  static fromResponse(response: Response, body: unknown): ApiError {
    const p = (body && typeof body === 'object' ? body : {}) as Partial<Problem>;
    const status = response.status;
    return new ApiError({
      status,
      code: p.code ?? (status === 0 ? 'network' : `http_${status}`),
      title: p.title ?? '',
      detail: p.detail ?? fallbackDetail(status),
      errors: Array.isArray(p.errors) ? p.errors : [],
      requestId: p.requestId,
    });
  }

  static network(): ApiError {
    return new ApiError({
      status: 0,
      code: 'network',
      title: 'Немає з’єднання',
      detail: 'Не вдалося зв’язатися із сервером. Перевірте мережу — дію буде можна повторити.',
    });
  }

  /** Дію збережено в офлайн-черзі й буде надіслано пізніше. */
  get isQueued(): boolean {
    return this.code === 'queued_offline';
  }

  /** Перше повідомлення для поля (точний збіг або вкладене поле, напр. customFields.<id>). */
  fieldError(field: string): string | undefined {
    return this.errors.find((e) => e.field === field)?.message;
  }

  /** Повідомлення, які не показано біля полів форми (щоб не дублювати їх у загальному блоці). */
  messagesExcept(shownFields: string[]): string[] {
    return this.errors.filter((e) => !e.field || !shownFields.includes(e.field)).map((e) => e.message);
  }

  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.errors) if (e.field && !out[e.field]) out[e.field] = e.message;
    return out;
  }

  get isRuleViolation() {
    return this.status === 422 && this.code === 'rule_violation';
  }

  get isSensitive() {
    return this.code === 'sensitive_data';
  }

  get isConflictVersion() {
    return this.status === 412;
  }
}

function fallbackDetail(status: number): string {
  if (status === 401) return 'Потрібно увійти в систему.';
  if (status === 403) return 'Недостатньо прав для цієї дії.';
  if (status === 404) return 'Об’єкт не знайдено.';
  if (status === 409) return 'Конфлікт даних. Оновіть сторінку й повторіть.';
  if (status === 412) return 'Об’єкт змінено іншим користувачем. Оновіть дані й повторіть.';
  if (status === 429) return 'Забагато запитів. Повторіть за хвилину.';
  if (status >= 500) return 'Внутрішня помилка сервера. Спробуйте пізніше.';
  return 'Не вдалося виконати запит.';
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Людське повідомлення для будь-якої помилки. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.detail;
  if (e instanceof Error) return e.message;
  return 'Сталася помилка.';
}
